/*
 * Copyright (c) CIRROM 2026.
 */

import { useEffect, useState } from "react";
import {
  AppliedChange,
  getAppliedChanges,
} from "../../../api/suggestedchanges";

/**
 * The applied-change history for one contact or account.
 *
 * Returns null until the request answers, and stays null if it fails: the
 * history is a secondary view whose latency is unknown, so the surfaces that
 * show it only add their tab once there is something real to put in it, and
 * never block or error on its account.
 *
 * Keyed on the CRM id, never record_id - record_id changes every batch, so it
 * cannot reach history from earlier batches. `refreshKey` lets a host refetch
 * after a write it made, without the stale list blanking in between.
 */
export const useAppliedChanges = (args: {
  integratedSystemId?: number | null;
  entityType?: "CONTACT" | "ACCOUNT" | null;
  crmId?: string | null;
  refreshKey?: number;
}): AppliedChange[] | null => {
  const { integratedSystemId, entityType, crmId, refreshKey } = args;
  const [changes, setChanges] = useState<AppliedChange[] | null>(null);

  /* A different record's history must never show under this one. */
  useEffect(() => {
    setChanges(null);
  }, [integratedSystemId, entityType, crmId]);

  useEffect(() => {
    if (!integratedSystemId || !entityType || !crmId) {
      return undefined;
    }
    let cancelled = false;
    Promise.resolve()
      .then(() =>
        getAppliedChanges(integratedSystemId, entityType, String(crmId))
      )
      .then((response) => {
        if (!cancelled) setChanges(Array.isArray(response) ? response : []);
      })
      .catch(() => {
        // Keep whatever answered last; the tab is a convenience.
      });
    return () => {
      cancelled = true;
    };
  }, [integratedSystemId, entityType, crmId, refreshKey]);

  return changes;
};
